import { setSidebarItemsState, SidebarItemId, SidebarItemState } from '@/store/slices/ui-state.slice'
import { useAppDispatch } from '@/store/store-hooks'
import { loadSidebarItemsStateFromLocalStorage } from '@/util/storage-utils'
import { ReactNode } from 'react'

// Define all sidebar items that should exist with their default states
const DEFAULT_SIDEBAR_ITEMS: SidebarItemState[] = [
  { itemId: SidebarItemId.MedicalOverview, isOpen: false },
  // Add other sidebar items here as they're added to the app
]

/**
 * Migrates sidebar state from localStorage, ensuring all required items exist
 * This handles cases where existing users don't have new sidebar items
 */
function migrateSidebarState(loadedState: SidebarItemState[]): SidebarItemState[] {
  const migratedState = [...loadedState]

  // Add any missing items from defaults
  DEFAULT_SIDEBAR_ITEMS.forEach((defaultItem) => {
    const exists = migratedState.some((item) => item.itemId === defaultItem.itemId)
    if (!exists) {
      console.log(`[AppInit] Adding missing sidebar item: ${defaultItem.itemId}`)
      migratedState.push(defaultItem)
    }
  })

  return migratedState
}

/**
 * AppInit Component
 * - Component to handle application initialization tasks
 * - Happens before authentication
 *
 * BLOCKING:
 * - Can be used to block the application until initialization is complete
 * - Conditionally render children based on initialization status
 *
 * NON-BLOCKING:
 * - Can be used to execute startup functions in the background that are not-blocking
 */
export const AppInit = ({ children }: { children: ReactNode }) => {
  const dispatch = useAppDispatch()

  // Restore sidebar state from localStorage synchronously
  const loadedSidebarItemsState = loadSidebarItemsStateFromLocalStorage()
  if (loadedSidebarItemsState) {
    // Migrate the loaded state to ensure all required items exist
    const migratedState = migrateSidebarState(loadedSidebarItemsState)
    dispatch(setSidebarItemsState(migratedState))
  } else {
    // No saved state, use defaults
    dispatch(setSidebarItemsState(DEFAULT_SIDEBAR_ITEMS))
  }

  // // EXAMPLE IF BLOCING EVER NEEDED
  // // Blocking loader UX
  // if (loading) {
  //   return <FullPageLoader />
  // }

  return children
}
